import { useState, type FormEvent } from "react";
import type { AppLocale } from "../i18n";
import { CheckoutApiError, privateCheckoutPath, startOrderRecovery, verifyOrderRecovery } from "../lib/api";
import { BRAND } from "../lib/brand";
import { flowCopy } from "./flow-copy";
import { useVerificationCooldown, verificationError } from "./verification-feedback";

export function RecoverAccess({ token, locale, onVerified }: {
  token: string; locale: AppLocale; onVerified: (path: string) => void
}) {
  const copy = flowCopy(locale);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cooldown = useVerificationCooldown(locale);

  async function start() {
    if (busy || cooldown.waiting) return;
    setBusy(true); setError(null);
    try { setVerificationId((await startOrderRecovery(token)).verificationId); setCode(""); cooldown.start(); }
    catch (cause) {
      setError(verificationError(cause, locale, copy.recoverFailed));
      if (cause instanceof CheckoutApiError && cause.code === "verification_rate_limited") cooldown.start();
    } finally { setBusy(false); }
  }

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!verificationId || !/^\d{8}$/.test(code) || busy) return;
    setBusy(true); setError(null);
    try {
      const result = await verifyOrderRecovery(token, verificationId, code);
      const path = privateCheckoutPath(result.checkoutUrl);
      if (!path) throw new Error("Invalid private checkout URL");
      setCode(""); onVerified(path);
    } catch (cause) { setError(verificationError(cause, locale, copy.codeFailed)); } finally { setBusy(false); }
  }

  return <main className="co-page co-handoff"><section className="co-card co-handoff-form">
    <h1 className="co-card-title">{verificationId ? copy.codeTitle : copy.recoverTitle}</h1>
    <p>{verificationId ? copy.codeIntro : copy.recoverIntro}</p>
    {verificationId ? <form className="co-handoff-form" onSubmit={event => void verify(event)}>
      <label>{copy.code}<input autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{8}" maxLength={8}
        value={code} onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 8))} /></label>
      <button className="co-cta" type="submit" disabled={busy || !/^\d{8}$/.test(code)}>{busy ? copy.verifying : copy.verify}</button>
      <button className="co-btn-secondary" type="button" disabled={busy || cooldown.waiting}
        onClick={() => void start()}>{cooldown.label}</button>
    </form> : <button className="co-cta" type="button" disabled={busy || cooldown.waiting} onClick={() => void start()}>{busy ? copy.sending : cooldown.waiting ? cooldown.label : copy.recoverSend}</button>}
    {error && <p role="alert" className="co-handoff-error">{error}</p>}
    <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{copy.support}</a>
  </section></main>;
}
